import { useState } from "react";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import { X } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { GALLERY, GALLERY_CATS } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";
import { Seo } from "@/components/Seo";

const spanClass = (s) => (s === "lg" ? "md:col-span-2" : s === "tall" ? "md:row-span-2" : "");

export default function Gallery() {
  const [cat, setCat] = useState("All");
  const [active, setActive] = useState(null);
  const { t, lang } = useLang();
  const g = t.gallery;
  const title = (x) => (lang === "es" ? x.titleEs : x.title);
  const items = cat === "All" ? GALLERY : GALLERY.filter((x) => x.cat === cat);

  return (
    <main data-testid="gallery-page">
      <Seo
        title="Fresh Produce Gallery — Peppers, Chilies, Melons & More | Sebastian's Produce"
        description="Photos of the colored bell peppers, green chilies, tomatoes, squash, cucumbers, herbs, melons and fruit that move through Sebastian's Produce in Los Angeles every morning."
      />
      <PageHero key={lang} eyebrow={g.eyebrow} lines={g.lines} text={g.text} testId="gallery-hero" />

      <section className="pb-24 lg:pb-32" data-testid="gallery-grid-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <LayoutGroup>
            <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
              {GALLERY_CATS.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  data-testid={`gallery-filter-${c.toLowerCase()}`}
                  className={`relative rounded-full px-4 py-2 text-sm font-medium transition-colors ${cat === c ? "text-white" : "text-[var(--ink-2)] hover:text-[var(--ink)]"}`}
                >
                  {cat === c && <motion.span layoutId="filter-pill" className="absolute inset-0 rounded-full bg-[var(--ink)]" transition={{ type: "spring", stiffness: 400, damping: 32 }} />}
                  <span className="relative">{g.cats[c]}</span>
                </button>
              ))}
            </div>
          </LayoutGroup>

          <motion.div layout className="mt-10 grid grid-flow-dense grid-cols-2 gap-4 md:auto-rows-[240px] md:grid-cols-4" data-testid="gallery-grid">
            <AnimatePresence mode="popLayout">
              {items.map((x) => (
                <motion.button
                  layout
                  key={x.title}
                  initial={{ opacity: 0, scale: 0.92 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.92 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => setActive(x)}
                  className={`img-zoom group relative aspect-square overflow-hidden rounded-2xl md:aspect-auto ${spanClass(x.span)}`}
                  data-testid={`gallery-item-${x.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  <img src={x.src} alt={title(x)} className="h-full w-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  <div className="absolute bottom-4 left-4 translate-y-2 text-left text-white opacity-0 transition-[opacity,transform] duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                    <p className="font-mono-label text-[10px] text-white/70">{g.cats[x.cat]}</p>
                    <p className="font-display text-lg font-semibold">{title(x)}</p>
                  </div>
                </motion.button>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-[var(--ink)]/90 p-6 backdrop-blur-md"
            onClick={() => setActive(null)}
            data-testid="gallery-lightbox"
          >
            <button className="absolute right-6 top-6 rounded-full bg-white/10 p-3 text-white hover:bg-white/20" aria-label="Close" data-testid="gallery-lightbox-close">
              <X size={20} />
            </button>
            <motion.figure
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="max-h-[85vh] max-w-5xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={active.src} alt={title(active)} className="max-h-[75vh] w-auto rounded-2xl object-contain" />
              <figcaption className="mt-4 flex items-center justify-between text-white">
                <span className="font-display text-xl font-semibold">{title(active)}</span>
                <span className="font-mono-label text-[10px] text-white/60">{g.cats[active.cat]}</span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
